/**
 * Component Name: OneTrust Placeholder
 *
 * This component is used in the OneTrustFilter component to display a placeholder
 * in place of 3rd party script elements
 */
import { useId } from 'react';
import styles from './OneTrustPlaceholder.module.scss';
import useDictionary from '../../../../lib/hooks/useDictionary';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { SvgIcon } from 'components/helpers/SvgIcon';

interface IOneTrustPlaceholder {
  featureType: string | undefined;
}

export default function OneTrustPlaceholder({ featureType }: IOneTrustPlaceholder) {
  // Translations
  const { getDictionaryValue } = useDictionary();

  // Generate random Id for placeholder text
  const textId = useId();

  // Directly call OneTrust API — needed for dynamically rendered placeholders
  // (e.g. inside VerticalVideoModal) that are not present when the global click binder runs on page load.
  const handleOpenPreferenceCenter = () => {
    if (typeof window !== 'undefined' && window.OneTrust) {
      window.OneTrust.ToggleInfoDisplay();
    }
  };

  let featureConsentHeadline = '';

  switch (featureType) {
    case 'video':
      featureConsentHeadline = getDictionaryValue('Common.OneTrustSettings.VideoConsentHeadline');
      break;
    // TODO: Add other feature types checks as needed, and create the corresponding dictionary items
    case 'buynow':
      featureConsentHeadline = getDictionaryValue('Common.OneTrustSettings.BuyNowConsentHeadline');
      break;
    // case 'social':
    //   featureConsentHeadline = getDictionaryValue('Common.OneTrustSettings.SocialConsentHeadline');
    //   break;
    case 'ratings':
      featureConsentHeadline = getDictionaryValue('Common.OneTrustSettings.RatingsConsentHeadline');
      break;
    case 'productlocator':
      featureConsentHeadline = getDictionaryValue(
        'Common.OneTrustSettings.ProductLocatorConsentHeadline'
      );
      break;
    case 'retailerlist':
      featureConsentHeadline = getDictionaryValue(
        'Common.OneTrustSettings.RetailerListConsentHeadline'
      );
      break;
    default:
      featureConsentHeadline = '';
  }

  return (
    <div className={styles.consentPlaceholder}>
      <div className={styles.copyContainer} id={textId}>
        {featureConsentHeadline && (
          <div className={`${styles.consentHeading}`}>{featureConsentHeadline}</div>
        )}
        <p className={styles.consentBody}>
          {getDictionaryValue('Common.OneTrustSettings.ConsentBody')}
        </p>
      </div>

      <CtaButton
        type="button"
        aria-describedby={textId}
        data-gmiot="gmi-onetrust-preference-link"
        ctaStyle="outline"
        ctaVariant="brand"
        onClick={handleOpenPreferenceCenter}
        className={styles.consentButton}
      >
        <SvgIcon viewBox="0 0 24 24" icon="leading" />
        <span>{getDictionaryValue('Common.OneTrustSettings.CookieSettingLabel')}</span>
      </CtaButton>
    </div>
  );
}
